// Relative imports, not the '@' alias: jest has no moduleNameMapper for '@'
// and jest.config.js may not be touched. See useCourseList.ts.
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';

import {CourseJob} from './types';

/**
 * Courses built from the puzzles a coach launched in class.
 *
 * When a coach launches a puzzle in the classroom tool, the exact position,
 * the coach's own line and the thinking time are saved at that instant. For
 * those recordings a course needs no board-reading off the video at all, so
 * the admin flow is: pick the recording, see every launched puzzle — what will
 * be used, what is left out and why — then build.
 *
 * Shapes follow the spec's API section
 * (chess/docs/superpowers/specs/2026-10-04-active-puzzle-courses-design.md).
 */

async function authHeader() {
  const token = await getLocalStorage('access_token');
  return {Authorization: `Bearer ${token}`};
}

/** A recording is offered only with at least this many usable puzzles. */
export const MIN_KEPT = 3;

export type ActivePuzzleRecording = {
  video_course_id: number;
  title: string;
  coach: string;
  category: string;
  /** ISO, UTC-aware. Null when the recording carries no start. */
  recording_start: string | null;
  duration_sec: number | null;
};

export type ActivePuzzleSummary = {
  launched: number;
  kept: number;
  /** Reason (in words) → how many launched puzzles were left out for it. */
  dropped: Record<string, number>;
};

export type LaunchedPuzzle = {
  id: number;
  name: string | null;
  launched_at_sec: number;
  /** "mm:ss" into the recording. */
  launched_at: string;
  fen: string;
  side: 'white' | 'black';
  /** The coach's line, replayed from the FEN. Empty when it does not replay. */
  moves_san: string[];
  /** The coach's line as stored, in UCI — present even when it does not replay. */
  moves_uci?: string[];
  plies: number;
  time_limit_sec: number | null;
  mystery_bonus: boolean;
  orientation: 'white' | 'black' | null;
  status: 'kept' | 'dropped';
  reason: string | null;
  warning: string | null;
};

export type ActivePuzzleReport = {
  recording: ActivePuzzleRecording;
  summary: ActivePuzzleSummary;
  puzzles: LaunchedPuzzle[];
};

export type CreateActivePuzzleCoursePayload = {
  video_course_id: number;
  force?: boolean;
};

export async function fetchActivePuzzles(
  videoCourseId: number,
): Promise<ActivePuzzleReport> {
  const response = await ChessRestClient.get(
    `/video_learning/masterclasses/${videoCourseId}/active-puzzles/`,
    {headers: await authHeader()},
  );
  return response.data as ActivePuzzleReport;
}

/**
 * Same response as the existing create endpoint — a job, polled by the
 * screen's job card exactly as any other build.
 */
export async function createActivePuzzleCourse(
  payload: CreateActivePuzzleCoursePayload,
): Promise<CourseJob> {
  // `force` only when rebuilding. Never sent as false "for completeness": the
  // one that matters is the true, and it overwrites a course.
  const body: CreateActivePuzzleCoursePayload = {
    video_course_id: payload.video_course_id,
  };
  if (payload.force) body.force = true;
  const response = await ChessRestClient.post(
    '/video_learning/courses/active-puzzles/',
    body,
    {headers: await authHeader()},
  );
  return response.data as CourseJob;
}

/** Every launched puzzle of one recording, kept and left out. */
export function useActivePuzzles(
  videoCourseId: number | null,
): UseQueryResult<ActivePuzzleReport, Error> {
  return useQuery<ActivePuzzleReport, Error>(
    ['masterclass-active-puzzles', videoCourseId],
    () => fetchActivePuzzles(videoCourseId as number),
    {enabled: videoCourseId != null, staleTime: 60 * 1000},
  );
}

export function useCreateActivePuzzleCourse(): UseMutationResult<
  CourseJob,
  unknown,
  CreateActivePuzzleCoursePayload
> {
  return useMutation<CourseJob, unknown, CreateActivePuzzleCoursePayload>(
    createActivePuzzleCourse,
  );
}

// ---- words -----------------------------------------------------------------

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

const leftOut = (summary: ActivePuzzleSummary) =>
  Object.values(summary?.dropped ?? {}).reduce(
    (total, n) => total + (Number(n) || 0),
    0,
  );

/** "14 launched · 11 will be used · 3 left out". */
export function summaryLine(summary: ActivePuzzleSummary): string {
  return (
    `${summary.launched} launched · ${summary.kept} will be used · ` +
    `${leftOut(summary)} left out`
  );
}

/** "no_solution_stored" → "No solution stored"; prose passes through. */
export function reasonText(reason: string): string {
  const words = (reason || '').includes(' ')
    ? reason
    : (reason || '').replace(/_/g, ' ');
  const trimmed = words.trim();
  return trimmed ? trimmed[0].toUpperCase() + trimmed.slice(1) : trimmed;
}

/** Each reason a puzzle was left out, with its count, most common first. */
export function droppedReasons(
  summary: ActivePuzzleSummary,
): {reason: string; count: number}[] {
  return Object.entries(summary?.dropped ?? {})
    .map(([reason, count]) => ({
      reason: reasonText(reason),
      count: Number(count) || 0,
    }))
    .filter(row => row.count > 0)
    .sort((a, b) => b.count - a.count);
}

/** "Create course from 11 active puzzles". */
export function createLabel(kept: number): string {
  return `Create course from ${plural(
    kept,
    'active puzzle',
    'active puzzles',
  )}`;
}

/** Why the create action is unavailable, or null when it is available. */
export function createBlockedReason(kept: number): string | null {
  if (kept >= MIN_KEPT) return null;
  const have =
    kept <= 0
      ? 'none of the launched puzzles can be used'
      : `only ${plural(kept, 'puzzle', 'puzzles')} can be used`;
  return (
    `A course needs at least ${MIN_KEPT} puzzles the coach launched in ` +
    `class, and here ${have}. Pick another recording.`
  );
}

/** The picker tile's badge: "11 active puzzles". */
export function activeBadge(kept: number): string {
  return plural(kept, 'active puzzle', 'active puzzles');
}

/** "5 min", "30 sec", "1 min 30 sec"; '' when there is no limit. */
export function thinkingTime(seconds: number | null | undefined): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '';
  const whole = Math.round(seconds);
  const mins = Math.floor(whole / 60);
  const secs = whole % 60;
  if (!mins) return `${secs} sec`;
  return secs ? `${mins} min ${secs} sec` : `${mins} min`;
}

/**
 * The coach's line with move numbers, as it reads on a scoresheet:
 * "12. Qd8+ Kh7 13. Qe7", or "12… Kh7 13. Qd3+" when Black starts.
 */
export function sanLine(moves: string[], fen: string): string {
  if (!moves?.length) return '';
  const fields = (fen || '').trim().split(/\s+/);
  let whiteToMove = fields[1] !== 'b';
  let number = Number(fields[5]);
  if (!Number.isFinite(number) || number < 1) number = 1;

  const parts: string[] = [];
  moves.forEach((san, index) => {
    if (whiteToMove) parts.push(`${number}. ${san}`);
    else parts.push(index === 0 ? `${number}… ${san}` : san);
    if (!whiteToMove) number += 1;
    whiteToMove = !whiteToMove;
  });
  return parts.join(' ');
}

export type CreateErrorView = {
  kind: 'exists' | 'too-few' | 'other';
  message: string;
};

/**
 * Words that mean the server is talking to a developer: request fields,
 * literals, exception text. None of it belongs in front of an admin.
 */
const DEVELOPER_TEXT =
  /\b(force|true|false|null|none|traceback|exception|status code)\b|[{}[\]<>=]|\b\w+_\w+\b|\w\.\w+\.\w|:\s*\w+(Error|Exception)\b/i;

/**
 * A server message, if it is plainly written for people — or null.
 *
 * Our endpoints mix the two: "No recording with id 104" reads fine, while
 * "Send force:true to rebuild it" is API documentation. Anything that names
 * a field, a literal or an exception is dropped in favour of our own words.
 */
export function humanServerText(text: unknown): string | null {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 200) return null;
  return DEVELOPER_TEXT.test(trimmed) ? null : trimmed;
}

/**
 * What to tell the admin when the create call is refused.
 *
 * 409 is not a failure so much as a question — a course already exists, and
 * the server will not overwrite it unless asked — so it is told apart and the
 * screen offers Rebuild. 409 and 422 are always in our words; the server's
 * text for them is written for API callers.
 */
export function describeCreateError(error: unknown): CreateErrorView {
  const response = (error as any)?.response;
  const status = response?.status;

  if (status === 409) {
    return {
      kind: 'exists',
      message: 'A course already exists for this recording.',
    };
  }
  if (status === 422) {
    return {
      kind: 'too-few',
      message:
        `This recording has fewer than ${MIN_KEPT} puzzles that can be used, ` +
        `so a course can't be built from it.`,
    };
  }
  return {
    kind: 'other',
    message:
      humanServerText(response?.data?.error) ??
      'Could not start the course. Try again.',
  };
}
